<template>
  <form
    autocomplete="off"
    @submit.prevent="submitContactUsForm()"
    id="contact-us-form"
    data-test="contact-us-form"
    class="md:px-6 text-left">
    <div v-if="isItMygoalgetter" class="text-16px text-center pb-4">
      Our support hotline is available:
      <a href="tel:0730180400" class="text-black" target="_blank">(07) 3018 0400</a>
    </div>

    <input-text
      class="w-full my-6"
      input-classes="paragraph-2 text-left"
      place-holder="Name"
      label="Name"
      name="contact-us-name"
      :value="name"
      @input="updateName"
      :error="errors.name" />
    <input-text
      type="email"
      class="w-full my-6"
      input-classes="paragraph-2 text-left"
      place-holder="Email address"
      label="Email"
      name="contact-us-email"
      :value="email"
      @input="updateEmail"
      :error="errors.email"
      :allow-digits="true" />
    <input-phone-number
      class="w-full my-6 gap-3"
      rows="120"
      place-holder="Enter your phone number"
      label="Phone"
      name="contact-phone-number"
      :value="phone_number"
      @input="updatePhoneNumber" />
    <input-text
      v-if="props.type === 'business'"
      class="w-full my-6"
      place-holder="Enter the name of your company"
      label="Company"
      name="business"
      :value="business"
      @input="updateBusiness"
      :error="errors.business"
      :allow-digits="true" />
    <input-select-box
      v-if="props.type === 'default'"
      class="w-full my-6"
      :options="messageTypes"
      label="What's your message about?"
      name="Message Type" />
    <contact-message-types
      class="md:flex items-center my-6 text-left"
      v-if="props.type === 'adviser' && isItSurmount"
      :message-types="surmountMessageTypes"
      :errors="errors.alternateType"
      @click="selectSurmountMessageType" />
    <contact-message-types
      class="md:flex items-center my-6 text-left"
      v-else-if="props.type === 'adviser'"
      :message-types="alternateMessageTypes"
      :errors="errors.alternateType"
      @click="selectMessageType" />
    <input-select-box
      v-if="props.type === 'adviser' && isItFordscott"
      :select-input="ageRange"
      @update-select-box="updateAgeRange"
      :options="ageBrackets"
      name="Age Brackets"
      class="md:flex my-6 text-left w-full"
      label="Age" />

    <input-text-area
      class="w-full my-6"
      rows="120"
      place-holder="Type in your message here"
      label="Message"
      name="contact-us-message"
      :value="message"
      @input="updateMessage"
      :error="errors.message" />
    <div class="flex justify-center">
      <custom-button
        :loading="loading"
        colour="white"
        class="w-52 bg-white text-black border-primary border my-6 text-center"
        @click="submitContactUsForm()">
        Send
      </custom-button>
    </div>
  </form>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import ContactMessageTypes from '@/components/BrandComponents/ContactUs/ContactMessageTypes.vue'
import CustomButton from '@/components/CustomButton/CustomButton.vue'
import InputPhoneNumber from '@/components/BasicInputs/InputPhoneNumber.vue'
import InputSelectBox from '@/components/BasicInputs/InputSelectBox.vue'
import InputText from '@/components/BasicInputs/InputText.vue'
import InputTextArea from '@/components/BasicInputs/InputTextArea.vue'
import MiscellaneousService from '@/services/MiscellaneousService'
import { useSessionStore } from '@/store/pinia/SessionStore'
import { useUserStore } from '@/store/pinia/UserStore'
import { alternateContactMessageTypes } from '@/lib/WhiteLabelHelpers'
import { validateEmailAddress } from '@/lib/ValidationHelper'
import { useToast } from '@/composables/useToast.ts'
import { store } from '@/store'

interface ContactFormProps {
  type?: string
}

interface FormErrors {
  name: string
  email: string
  message: string
  business: string
  alternateType?: string
}

interface MessageType {
  label: string
  value: string
  selected: boolean
  disabled: boolean
}

interface AlternateMessageType {
  name: string
  value: string
  selected: boolean
}

interface AgeBracket {
  label: string
  value: string | null
  selected?: boolean
}

const props = withDefaults(defineProps<ContactFormProps>(), {
  type: 'default'
})

const { successToast, errorToast } = useToast()
const sessionStore = useSessionStore()
const userStore = useUserStore()

const name = ref('')
const email = ref('')
const message = ref('')
const business = ref('')
const phone_number = ref(61)
const loading = ref(false)
const ageRange = ref<string | null>(null)
const alternateTypesSelected = ref(0)
const whitelabel_id = ref<number | null>(null)

const errors = reactive<FormErrors>({
  name: '',
  email: '',
  message: '',
  business: '',
  alternateType: ''
})

const messageTypes = ref<MessageType[]>([
  {
    label: 'General enquiry',
    value: 'general',
    selected: true,
    disabled: false,
  },
  {
    label: 'Feedback',
    value: 'feedback',
    selected: false,
    disabled: false,
  },
  {
    label: 'Media',
    value: 'media',
    selected: false,
    disabled: false,
  },
  {
    label: 'Careers',
    value: 'careers',
    selected: false,
    disabled: false,
  },
])

const surmountMessageTypes = ref<AlternateMessageType[]>([
  {
    name: 'Salary packaging',
    value: 'salary_packaging',
    selected: false,
  },
  {
    name: 'Insurance',
    value: 'insurance',
    selected: false,
  },
  {
    name: 'Other',
    value: 'other',
    selected: false,
  },
  {
    name: 'Superannuation',
    value: 'super',
    selected: false,
  },
  {
    name: 'Investments',
    value: 'investments',
    selected: false,
  },
])

const ageBrackets = ref<AgeBracket[]>([
  {
    label: '-',
    value: null,
    selected: true,
  },
  {
    label: '18-35',
    value: '18-35',
  },
  {
    label: '36-50',
    value: '36-50',
  },
  {
    label: '51-64',
    value: '51-64',
  },
  {
    label: '65+',
    value: '65+',
  },
])

const isItMygoalgetter = computed(() => sessionStore.getWhitelabelData.identifier === 'mygoalgetter')
const isItAhomeformumordad = computed(() => sessionStore.getWhitelabelData.identifier === 'ahomeformumordad')
const isItFordscott = computed(() => sessionStore.getWhitelabelData.identifier === 'fordscott')
const isItSurmount = computed(() => sessionStore.getWhitelabelData.identifier === 'surmount')

const alternateMessageTypes = computed(() => {
  const messageTypes = alternateContactMessageTypes()
  if (isItAhomeformumordad.value) {
    messageTypes.splice(0, 1, {
      name: 'Aged Care',
      value: 'aged_care',
      selected: false,
    })
  }
  return messageTypes
})

onMounted(() => {
  email.value = userStore.getProfile.email || ''
  name.value = userStore.getProfile.firstName || ''
  whitelabel_id.value = sessionStore.getWhitelabelData.id
})

const submitContactUsForm = async () => {
  if (validateContactUsForm()) {
    loading.value = true
    const payload: any = {
      firstName: name.value,
      email: email.value,
      message: message.value,
      phone_number: phone_number.value,
      whitelabel_id: whitelabel_id.value,
      business: business.value,
    }
    if (props.type === 'adviser') {
      payload.type = props.type
      const types = isItSurmount.value ? surmountMessageTypes.value : alternateMessageTypes.value
      payload.discussions = types
        .filter((el) => el.selected)
        .map((el) => el.value)
        .join(',')
    }
    if (props.type === 'adviser' && isItFordscott.value) payload.age_bracket = ageRange.value

    try {
      await MiscellaneousService.submitContactUsForm(payload)
      if (props.type === 'adviser') store.dispatch('toggleAdviserContactModal')
      loading.value = false
      successToast('We got your message, we will be in touch.')
      name.value = ''
      email.value = ''
      message.value = ''
      phone_number.value = 61
      store.dispatch('renderContactUsModal', false)
    } catch (err) {
      loading.value = false
      errorToast('Error occurred, please try again.')
      store.dispatch('renderContactUsModal', false)
      throw err
    }
  }
}

const validateContactUsForm = (): boolean => {
  errors.name = ''
  errors.email = ''
  errors.message = ''
  errors.business = ''
  errors.alternateType = ''

  let validated = true
  if (name.value === '') {
    validated = false
    errors.name = 'Please enter your name'
  }

  if (!validateEmailAddress(email.value)) {
    validated = false
    errors.email = 'Please valid email address'
  }

  if (message.value === '') {
    validated = false
    errors.message = 'Please provide a message'
  }

  if (business.value && business.value === '') {
    validated = false
    errors.business = 'Please provide your companies name, or submit a user contact form.'
  }

  if (props.type === 'adviser' && isItSurmount.value && alternateTypesSelected.value < 1) {
    validated = false
    errors.alternateType = 'Please select at least one of the message types available'
  }

  return validated
}

const selectSurmountMessageType = (value: boolean, index: number) => {
  value ? (alternateTypesSelected.value += 1) : (alternateTypesSelected.value -= 1)
  surmountMessageTypes.value[index].selected = value
}

const selectMessageType = (value: boolean, index: number) => {
  value ? (alternateTypesSelected.value += 1) : (alternateTypesSelected.value -= 1)
  alternateMessageTypes.value[index].selected = value
}

const updateAgeRange = (value: string) => {
  ageRange.value = value
}

const updateName = (value: string) => {
  name.value = value
}

const updateEmail = (value: string) => {
  email.value = value
}

const updatePhoneNumber = (value: number) => {
  phone_number.value = value
}

const updateBusiness = (value: string) => {
  business.value = value
}

const updateMessage = (value: string) => {
  message.value = value
}
</script>

<style scoped></style>
